/**
 * Collection sort hook - manages sort state with Jotai
 * Shared between grid and table views
 */
import { atom, useAtom } from 'jotai';

import { getPrice } from '@/lib/price';
import { CONDITION_ORDER, FINISH_ORDER, getColorSortValue, parseCollectorNumber, RARITY_ORDER } from '@/lib/sortConstants';
import type { CardPrices, Currency, Finish } from '@/types/card';

export type SortField =
  | 'name'
  | 'type'
  | 'set'
  | 'price'
  | 'quantity'
  | 'dateAdded'
  | 'color'
  | 'rarity'
  | 'edhrecRank'
  | 'cmc'
  | 'folder'
  | 'roles'
  | 'condition'
  | 'language'
  | 'finish';
export type SortDirection = 'asc' | 'desc';

export interface CollectionSort {
  field: SortField;
  direction: SortDirection;
}

export const SORT_FIELD_LABELS: Record<SortField, string> = {
  name: 'Name',
  type: 'Type',
  set: 'Set',
  price: 'Price',
  quantity: 'Quantity',
  dateAdded: 'Date Added',
  color: 'Color',
  rarity: 'Rarity',
  edhrecRank: 'EDHREC Rank',
  cmc: 'Mana Value',
  folder: 'Folder',
  roles: 'Roles',
  condition: 'Condition',
  language: 'Language',
  finish: 'Finish',
};

const defaultSort: CollectionSort = {
  field: 'name',
  direction: 'asc',
};

const sortAtom = atom<CollectionSort>(defaultSort);

export interface UseCollectionSortReturn {
  sort: CollectionSort;
  setSort: (sort: CollectionSort) => void;
  setField: (field: SortField) => void;
  toggleDirection: () => void;
  resetSort: () => void;
}

export default function useCollectionSort(): UseCollectionSortReturn {
  const [sort, setSortState] = useAtom(sortAtom);

  const setSort = (newSort: CollectionSort) => {
    setSortState(newSort);
  };

  const setField = (field: SortField) => {
    setSortState((prev) => ({
      field,
      // Reset to asc when changing field, toggle if same field
      direction: prev.field === field ? (prev.direction === 'asc' ? 'desc' : 'asc') : 'asc',
    }));
  };

  const toggleDirection = () => {
    setSortState((prev) => ({
      ...prev,
      direction: prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const resetSort = () => {
    setSortState(defaultSort);
  };

  return {
    sort,
    setSort,
    setField,
    toggleDirection,
    resetSort,
  };
}

/**
 * Get price for a card based on its finish and currency.
 * Uses pre-computed price fields when available (EnrichedCollectionCard),
 * falls back to getPrice() for other card shapes.
 */
function getCardPriceForSort(
  card: {
    price_usd?: number | null;
    price_eur?: number | null;
    finish: string;
    card: {
      prices: CardPrices;
    };
  },
  currency: Currency = 'USD',
): number {
  // Use pre-computed price if available
  if (currency === 'EUR' && card.price_eur !== undefined) return card.price_eur ?? 0;
  if (currency === 'USD' && card.price_usd !== undefined) return card.price_usd ?? 0;

  // Fallback to runtime parsing via shared getPrice
  const price = getPrice(card.card.prices, currency.toLowerCase() as 'usd' | 'eur', card.finish as Finish);
  return price ?? 0;
}

/**
 * Sort comparator for collection cards.
 * Uses pre-computed sort fields from EnrichedCollectionCard when available.
 */
export function createSortComparator<
  T extends {
    quantity: number;
    finish: string;
    condition?: string;
    language?: string;
    language_label?: string;
    folder_path: string | null;
    created_at: { toMillis: () => number };
    // Pre-computed sort fields (optional for backward compat)
    price_usd?: number | null;
    price_eur?: number | null;
    color_sort_value?: number;
    rarity_sort_order?: number;
    classification?: { active_roles: string[] };
    collector_number_sort?: { num: number; suffix: string };
    card: {
      name: string;
      type_line: string | null;
      set_name: string;
      collector_number: string;
      rarity: string;
      edhrec_rank?: number | null;
      cmc: number | null;
      color_identity: string[];
      prices: CardPrices;
    };
  },
>(sort: CollectionSort, currency: Currency = 'USD'): (a: T, b: T) => number {
  return (a: T, b: T): number => {
    let comparison = 0;

    switch (sort.field) {
      case 'name':
        comparison = a.card.name.localeCompare(b.card.name);
        break;
      case 'type':
        comparison = (a.card.type_line ?? '').localeCompare(b.card.type_line ?? '');
        break;
      case 'set': {
        comparison = a.card.set_name.localeCompare(b.card.set_name);
        // Secondary sort by collector number when sets are equal
        if (comparison === 0) {
          const cnA = a.collector_number_sort ?? parseCollectorNumber(a.card.collector_number);
          const cnB = b.collector_number_sort ?? parseCollectorNumber(b.card.collector_number);
          comparison = cnA.num - cnB.num;
          if (comparison === 0) {
            comparison = cnA.suffix.localeCompare(cnB.suffix);
          }
        }
        break;
      }
      case 'price':
        comparison = getCardPriceForSort(a, currency) - getCardPriceForSort(b, currency);
        break;
      case 'quantity':
        comparison = a.quantity - b.quantity;
        break;
      case 'dateAdded':
        comparison = a.created_at.toMillis() - b.created_at.toMillis();
        break;
      case 'color':
        comparison =
          (a.color_sort_value ?? getColorSortValue(a.card.color_identity)) -
          (b.color_sort_value ?? getColorSortValue(b.card.color_identity));
        break;
      case 'rarity':
        comparison = (a.rarity_sort_order ?? RARITY_ORDER[a.card.rarity] ?? 0) - (b.rarity_sort_order ?? RARITY_ORDER[b.card.rarity] ?? 0);
        break;
      case 'cmc':
        comparison = (a.card.cmc ?? 0) - (b.card.cmc ?? 0);
        break;
      case 'edhrecRank': {
        const rankA = a.card.edhrec_rank ?? null;
        const rankB = b.card.edhrec_rank ?? null;
        if (rankA === rankB) return a.card.name.localeCompare(b.card.name);
        if (rankA === null) return 1;
        if (rankB === null) return -1;
        comparison = rankA - rankB;
        break;
      }
      case 'folder':
        // Cards without folders sort last, then alphabetically by path
        if (!a.folder_path && !b.folder_path) comparison = 0;
        else if (!a.folder_path) comparison = 1;
        else if (!b.folder_path) comparison = -1;
        else comparison = a.folder_path.localeCompare(b.folder_path);
        break;
      case 'roles':
        comparison = (a.classification?.active_roles?.length ?? 0) - (b.classification?.active_roles?.length ?? 0);
        break;
      case 'condition':
        comparison = (CONDITION_ORDER[a.condition ?? 'NM'] ?? 99) - (CONDITION_ORDER[b.condition ?? 'NM'] ?? 99);
        break;
      case 'language':
        comparison = (a.language_label ?? a.language ?? 'en').localeCompare(b.language_label ?? b.language ?? 'en');
        break;
      case 'finish':
        comparison = (FINISH_ORDER[a.finish] ?? 99) - (FINISH_ORDER[b.finish] ?? 99);
        break;
    }

    return sort.direction === 'asc' ? comparison : -comparison;
  };
}
